import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
  Button,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  Divider,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Fade,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  CalendarMonth,
  EventAvailable,
  EventBusy,
  LocationOn,
  Person,
  TrendingUp,
  FitnessCenter,
  Timer,
  Timeline,
  BarChart,
  PieChart,
  CheckCircle,
  Cancel,
  Warning,
  FilterList,
  Search,
  ExpandLess,
  LocalFireDepartment,
  Star
} from '@mui/icons-material';
// Using MUI Date Pickers for calendar functionality
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { formatDateSafe, toDate } from '../../shared/utils/dateHelpers';
import { formatStaffName } from '../../shared/utils/nameHelpers';
import { subDays, isWithinInterval, format } from 'date-fns';
import Badge from '@mui/material/Badge';

interface Session {
  session_date: string;
  service_type: string;
  pricing_option: string;
  staff_name: string;
  location_name: string;
  duration_minutes: number;
  service_category: string;
  no_show: boolean;
  late_cancel: boolean;
}

interface Appointment {
  date: { value: string };
  coach: string;
  location: string;
}

interface ClientSessionsViewProps {
  client: any;
  upcomingAppointments?: Appointment[];
  sessionHistory: Session[];
}

// Enhanced Calendar Component using MUI DateCalendar
const CalendarView: React.FC<{
  sessions: Session[];
  upcomingAppointments: Appointment[];
}> = ({ sessions, upcomingAppointments }) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  
  // Group sessions and appointments by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, { sessions: Session[], appointments: Appointment[] }>();
    
    // Add sessions
    sessions.forEach(session => {
      const date = toDate(session.session_date);
      if (date) {
        const key = format(date, 'yyyy-MM-dd');
        if (!map.has(key)) {
          map.set(key, { sessions: [], appointments: [] });
        }
        map.get(key)!.sessions.push(session);
      }
    });
    
    // Add appointments
    upcomingAppointments.forEach(apt => {
      const date = toDate(apt.date.value);
      if (date) {
        const key = format(date, 'yyyy-MM-dd');
        if (!map.has(key)) {
          map.set(key, { sessions: [], appointments: [] });
        }
        map.get(key)!.appointments.push(apt);
      }
    });
    
    return map;
  }, [sessions, upcomingAppointments]);
  
  // Custom day component with indicators
  const CustomDay = (props: PickersDayProps<Date> & { day?: Date }) => {
    const { day, ...other } = props;
    const dateKey = day ? format(day, 'yyyy-MM-dd') : '';
    const dayEvents = eventsByDate.get(dateKey);
    
    const hasCompletedSessions = dayEvents?.sessions.some(s => !s.no_show && !s.late_cancel) || false;
    const hasNoShows = dayEvents?.sessions.some(s => s.no_show) || false;
    const hasCancellations = dayEvents?.sessions.some(s => s.late_cancel) || false;
    const hasAppointments = (dayEvents?.appointments.length || 0) > 0;
    
    const totalEvents = (dayEvents?.sessions.length || 0) + (dayEvents?.appointments.length || 0);
    
    return (
      <Tooltip
        title={
          totalEvents > 0 ? (
            <Box>
              {dayEvents?.sessions.map((s, idx) => (
                <Typography key={idx} variant="caption" display="block">
                  {s.service_type} - {s.no_show ? 'No Show' : s.late_cancel ? 'Cancelled' : 'Completed'}
                </Typography>
              ))}
              {dayEvents?.appointments.map((a, idx) => (
                <Typography key={`apt-${idx}`} variant="caption" display="block">
                  📅 Upcoming: {formatStaffName(a.coach)}
                </Typography>
              ))}
            </Box>
          ) : ''
        }
        placement="top"
      >
        <Badge
          badgeContent={totalEvents}
          color={hasNoShows ? 'error' : hasCancellations ? 'warning' : hasCompletedSessions ? 'success' : hasAppointments ? 'info' : 'default'}
          overlap="circular"
          variant={totalEvents > 0 ? 'dot' : undefined}
        >
          <PickersDay {...other} day={day} />
        </Badge>
      </Tooltip>
    );
  };
  
  // Get selected date events
  const selectedDateEvents = selectedDate 
    ? eventsByDate.get(format(selectedDate, 'yyyy-MM-dd')) 
    : null;
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            slots={{
              day: CustomDay as any
            }}
            sx={{
              width: '100%',
              '& .MuiPickersCalendarHeader-root': {
                paddingLeft: 2,
                paddingRight: 2,
              }
            }}
          />
        </LocalizationProvider>
      </Grid>
      
      <Grid item xs={12} md={5}>
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a date'}
          </Typography>
          
          {selectedDateEvents ? (
            <Box>
              {/* Sessions */}
              {selectedDateEvents.sessions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Sessions ({selectedDateEvents.sessions.length})
                  </Typography>
                  <Stack spacing={1}>
                    {selectedDateEvents.sessions.map((session, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          backgroundColor: session.no_show 
                            ? alpha(theme.palette.error.main, 0.1)
                            : session.late_cancel
                              ? alpha(theme.palette.warning.main, 0.1)
                              : alpha(theme.palette.success.main, 0.1),
                          borderLeft: `3px solid ${
                            session.no_show 
                              ? theme.palette.error.main
                              : session.late_cancel
                                ? theme.palette.warning.main
                                : theme.palette.success.main
                          }`
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {session.service_type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatStaffName(session.staff_name)} • {session.location_name}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            session.no_show ? 'No Show' : 
                            session.late_cancel ? 'Cancelled' : 
                            'Completed'
                          }
                          color={
                            session.no_show ? 'error' : 
                            session.late_cancel ? 'warning' : 
                            'success'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
              
              {/* Appointments */}
              {selectedDateEvents.appointments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Upcoming Appointments ({selectedDateEvents.appointments.length})
                  </Typography>
                  <Stack spacing={1}>
                    {selectedDateEvents.appointments.map((apt, idx) => (
                      <Paper
                        key={`apt-${idx}`}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          borderLeft: `3px solid ${theme.palette.info.main}`
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          Scheduled Session
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatStaffName(apt.coach)} • {apt.location}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          {formatDateSafe(toDate(apt.date.value), 'h:mm a')}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
              
              {selectedDateEvents.sessions.length === 0 && selectedDateEvents.appointments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No events on this date
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a date to view details
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const ClientSessionsView: React.FC<ClientSessionsViewProps> = ({
  client,
  upcomingAppointments = [],
  sessionHistory = []
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'analytics'>('list');
  const [dateFilter, setDateFilter] = useState<'all' | 'week' | 'month' | 'quarter'>('all');
  const [coachFilter, setCoachFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Calculate session statistics
  const sessionStats = useMemo(() => {
    const now = new Date();
    const totalSessions = sessionHistory.length;
    const completedSessions = sessionHistory.filter(s => !s.no_show && !s.late_cancel).length;
    const noShows = sessionHistory.filter(s => s.no_show).length;
    const lateCancels = sessionHistory.filter(s => s.late_cancel).length;
    
    // Calculate attendance rate
    const attendanceRate = totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0;
    
    // Sessions by time period
    const lastWeek = sessionHistory.filter(s => {
      const sessionDate = toDate(s.session_date);
      return sessionDate && isWithinInterval(sessionDate, {
        start: subDays(now, 7),
        end: now
      });
    }).length;
    
    const lastMonth = sessionHistory.filter(s => {
      const sessionDate = toDate(s.session_date);
      return sessionDate && isWithinInterval(sessionDate, {
        start: subDays(now, 30),
        end: now
      });
    }).length;
    
    // Average sessions per week
    const firstSessionDate = sessionHistory.length > 0 
      ? toDate(sessionHistory[sessionHistory.length - 1].session_date)
      : null;
    const weeksSinceStart = firstSessionDate 
      ? Math.ceil((now.getTime() - firstSessionDate.getTime()) / (7 * 24 * 60 * 60 * 1000))
      : 0;
    const avgSessionsPerWeek = weeksSinceStart > 0 ? totalSessions / weeksSinceStart : 0;
    
    // Most frequent coach
    const coachCounts = sessionHistory.reduce((acc, session) => {
      acc[session.staff_name] = (acc[session.staff_name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const favoriteCoach = Object.entries(coachCounts)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';
    
    // Most frequent location
    const locationCounts = sessionHistory.reduce((acc, session) => {
      acc[session.location_name] = (acc[session.location_name] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const favoriteLocation = Object.entries(locationCounts)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';
    
    // Total training hours
    const totalMinutes = sessionHistory
      .filter(s => !s.no_show && !s.late_cancel)
      .reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    const totalHours = Math.round(totalMinutes / 60);
    
    return {
      totalSessions,
      completedSessions,
      noShows,
      lateCancels,
      attendanceRate,
      lastWeek,
      lastMonth,
      avgSessionsPerWeek,
      favoriteCoach,
      favoriteLocation,
      totalHours
    };
  }, [sessionHistory]);

  // Get unique coaches and locations for filters
  const coaches = useMemo(() => {
    const uniqueCoaches = new Set(sessionHistory.map(s => s.staff_name));
    return Array.from(uniqueCoaches).sort();
  }, [sessionHistory]);

  const locations = useMemo(() => {
    const uniqueLocations = new Set(sessionHistory.map(s => s.location_name));
    return Array.from(uniqueLocations).sort();
  }, [sessionHistory]);

  // Filter sessions
  const filteredSessions = useMemo(() => {
    let filtered = [...sessionHistory];
    
    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate: Date;
      
      switch (dateFilter) {
        case 'week':
          startDate = subDays(now, 7);
          break;
        case 'month':
          startDate = subDays(now, 30);
          break;
        case 'quarter':
          startDate = subDays(now, 90);
          break;
        default:
          startDate = new Date(0);
      }
      
      filtered = filtered.filter(s => {
        const sessionDate = toDate(s.session_date);
        return sessionDate && sessionDate >= startDate;
      });
    }
    
    // Coach filter
    if (coachFilter !== 'all') {
      filtered = filtered.filter(s => s.staff_name === coachFilter);
    }
    
    // Location filter
    if (locationFilter !== 'all') {
      filtered = filtered.filter(s => s.location_name === locationFilter);
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.service_type.toLowerCase().includes(search) ||
        s.staff_name.toLowerCase().includes(search) ||
        s.location_name.toLowerCase().includes(search)
      );
    }
    
    // Sort by date (newest first)
    return filtered.sort((a, b) => {
      const dateA = toDate(a.session_date);
      const dateB = toDate(b.session_date);
      if (!dateA || !dateB) return 0;
      return dateB.getTime() - dateA.getTime();
    });
  }, [sessionHistory, dateFilter, coachFilter, locationFilter, searchTerm]);


  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Training Sessions
          </Typography>
          <Stack direction="row" spacing={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="list">
                <Timeline />
              </ToggleButton>
              <ToggleButton value="calendar">
                <CalendarMonth />
              </ToggleButton>
              <ToggleButton value="analytics">
                <BarChart />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              startIcon={showFilters ? <ExpandLess /> : <FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
            >
              Filters
            </Button>
          </Stack>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`
              }}
            >
              <FitnessCenter sx={{ fontSize: 30, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {sessionStats.totalSessions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Sessions
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
              }}
            >
              <CheckCircle sx={{ fontSize: 30, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {Math.round(sessionStats.attendanceRate)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Attendance Rate
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`
              }}
            >
              <Timer sx={{ fontSize: 30, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {sessionStats.totalHours}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Training Hours
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`
              }}
            >
              <TrendingUp sx={{ fontSize: 30, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {sessionStats.avgSessionsPerWeek.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sessions/Week
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateFilter}
                    label="Date Range"
                    onChange={(e) => setDateFilter(e.target.value as any)}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="week">Last Week</MenuItem>
                    <MenuItem value="month">Last Month</MenuItem>
                    <MenuItem value="quarter">Last Quarter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Coach</InputLabel>
                  <Select
                    value={coachFilter}
                    label="Coach"
                    onChange={(e) => setCoachFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Coaches</MenuItem>
                    {coaches.map(coach => (
                      <MenuItem key={coach} value={coach}>
                        {formatStaffName(coach)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={locationFilter}
                    label="Location"
                    onChange={(e) => setLocationFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Locations</MenuItem>
                    {locations.map(location => (
                      <MenuItem key={location} value={location}>
                        {location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      {/* Upcoming Appointments */}
      {upcomingAppointments.length > 0 && (
        <Fade in>
          <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EventAvailable sx={{ mr: 1, color: 'info.main' }} />
                Upcoming Appointments
              </Typography>
              <Grid container spacing={2}>
                {upcomingAppointments.slice(0, 3).map((appointment, index) => {
                  const appointmentDate = toDate(appointment.date.value);
                  const isToday = appointmentDate && format(appointmentDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  
                  return (
                    <Grid item xs={12} md={4} key={index}>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: isToday 
                            ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
                            : 'inherit',
                          borderLeft: `4px solid ${isToday ? theme.palette.success.main : theme.palette.info.main}`
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {formatDateSafe(appointmentDate, 'EEE, MMM d')}
                          </Typography>
                          {isToday && (
                            <Chip label="TODAY" color="success" size="small" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {formatDateSafe(appointmentDate, 'h:mm a')}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Person sx={{ fontSize: 16, mr: 0.5 }} />
                            {formatStaffName(appointment.coach)}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                            {appointment.location}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Main Content Based on View Mode */}
      {viewMode === 'list' && (
        <Fade in>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Timeline sx={{ mr: 1, color: 'primary.main' }} />
                Session History ({filteredSessions.length} sessions)
              </Typography>
              
              {filteredSessions.length > 0 ? (
                <List>
                  {filteredSessions.map((session, index) => {
                    const sessionDate = toDate(session.session_date);
                    const isNoShow = session.no_show;
                    const isCancelled = session.late_cancel;
                    
                    return (
                      <React.Fragment key={index}>
                        <ListItem
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                        >
                          <Box sx={{ width: '100%' }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{
                                      mr: 2,
                                      backgroundColor: isNoShow 
                                        ? theme.palette.error.main 
                                        : isCancelled 
                                          ? theme.palette.warning.main 
                                          : theme.palette.success.main
                                    }}
                                  >
                                    {isNoShow ? <Cancel /> : isCancelled ? <Warning /> : <CheckCircle />}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                      {formatDateSafe(sessionDate, 'MMM d, yyyy')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDateSafe(sessionDate, 'EEEE')} • {session.duration_minutes} min
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography variant="body2" fontWeight="medium">
                                  {session.service_type}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {session.service_category}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography variant="body2">
                                  <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                  {formatStaffName(session.staff_name)}
                                </Typography>
                                <Typography variant="body2">
                                  <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                  {session.location_name}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Chip
                                    label={
                                      isNoShow ? 'No Show' : 
                                      isCancelled ? 'Cancelled' : 
                                      'Completed'
                                    }
                                    color={
                                      isNoShow ? 'error' : 
                                      isCancelled ? 'warning' : 
                                      'success'
                                    }
                                    size="small"
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </ListItem>
                        {index < filteredSessions.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <EventBusy sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    No sessions found matching your filters
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      )}

      {viewMode === 'calendar' && (
        <Fade in>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarMonth sx={{ mr: 1, color: 'primary.main' }} />
                Session Calendar
              </Typography>
              <CalendarView 
                sessions={filteredSessions}
                upcomingAppointments={upcomingAppointments}
              />
            </CardContent>
          </Card>
        </Fade>
      )}

      {viewMode === 'analytics' && (
        <Fade in>
          <Box>
            <Grid container spacing={3}>
              {/* Training Insights */}
              <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <Star sx={{ mr: 1, color: 'warning.main' }} />
                      Training Insights
                    </Typography>
                    <List>
                      <ListItem>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Favorite Coach</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatStaffName(sessionStats.favoriteCoach)}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={100} 
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </ListItem>
                      <ListItem>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Preferred Location</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {sessionStats.favoriteLocation}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={90} 
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </ListItem>
                      <ListItem>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Consistency Score</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {Math.round(sessionStats.attendanceRate)}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={sessionStats.attendanceRate}
                            color={sessionStats.attendanceRate > 80 ? 'success' : 'warning'}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Attendance Patterns */}
              <Grid item xs={12} md={6}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <PieChart sx={{ mr: 1, color: 'secondary.main' }} />
                      Attendance Breakdown
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircle sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                            Completed
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {sessionStats.completedSessions} ({Math.round((sessionStats.completedSessions / sessionStats.totalSessions) * 100)}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(sessionStats.completedSessions / sessionStats.totalSessions) * 100}
                          color="success"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Cancel sx={{ fontSize: 16, mr: 1, color: 'error.main' }} />
                            No Shows
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {sessionStats.noShows} ({Math.round((sessionStats.noShows / sessionStats.totalSessions) * 100)}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(sessionStats.noShows / sessionStats.totalSessions) * 100}
                          color="error"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Warning sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                            Late Cancellations
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {sessionStats.lateCancels} ({Math.round((sessionStats.lateCancels / sessionStats.totalSessions) * 100)}%)
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(sessionStats.lateCancels / sessionStats.totalSessions) * 100}
                          color="warning"
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Activity */}
              <Grid item xs={12}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocalFireDepartment sx={{ mr: 1, color: 'error.main' }} />
                      Activity Streak
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="h3" fontWeight="bold" color="primary">
                            {sessionStats.lastWeek}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Sessions Last Week
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="h3" fontWeight="bold" color="secondary">
                            {sessionStats.lastMonth}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Sessions Last Month
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="h3" fontWeight="bold" color="success">
                            {sessionStats.avgSessionsPerWeek.toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Avg Sessions/Week
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default ClientSessionsView;